import { useState, useEffect } from "react";
import { Container, Card, Form, Button, Alert, Spinner, Badge, Row, Col, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { validatePassword } from "../utils/passwordValidation.js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// Stripe will be loaded dynamically from backend

// Custom Description Checkout Component
function CustomDescriptionCheckout({ onSuccess, onError, clientSecret, customDescriptionCost }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isReady, setIsReady] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) {
      setError("Stripe is not loaded. Please refresh the page.");
      return;
    }

    const paymentElement = elements.getElement("payment");
    if (!paymentElement) {
      setError("Payment form is not ready. Please wait a moment and try again.");
      return;
    }
    
    setProcessing(true);
    setError("");

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      const intentResult = await stripe.retrievePaymentIntent(clientSecret);
      
      if (!intentResult.paymentIntent) {
        setError("Unable to retrieve payment information. Please try again.");
        setProcessing(false);
        return;
      }

      if (intentResult.paymentIntent.status === "succeeded") {
        onSuccess(intentResult.paymentIntent.id);
      } else {
        setError(`Payment status: ${intentResult.paymentIntent.status}. Please try again.`);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
      setProcessing(false);
      if (onError) onError(err.message || "An unexpected error occurred");
    }
  };

  return (
    <>
      <PaymentElement 
        onReady={() => {
          setIsReady(true);
        }}
        options={{
          layout: "tabs"
        }}
      />
      {!isReady && (
        <Alert variant="info" className="mt-3 brutal-alert">
          <Spinner size="sm" className="me-2" />
          LOADING PAYMENT FORM...
        </Alert>
      )}
      <div className="d-flex gap-2 mt-3">
        <Button
          className="brutal-btn brutal-btn-secondary"
          onClick={() => {
            if (onError) onError("Payment cancelled");
          }}
          disabled={processing}
        >
          CANCEL
        </Button>
        <Button
          className="brutal-btn brutal-btn-primary flex-grow-1"
          disabled={processing || !isReady}
          onClick={submit}
        >
          {processing ? (
            <>
              <Spinner size="sm" className="me-2" />
              PROCESSING...
            </>
          ) : (
            `PAY $${(customDescriptionCost / 100).toFixed(2)} & UNLOCK`
          )}
        </Button>
      </div>
      {error && (
        <Alert variant="danger" className="brutal-alert mt-2">
          {error.toUpperCase()}
        </Alert>
      )}
    </>
  );
}

// Reroll Checkout Component
function RerollCheckout({ onSuccess, onError, clientSecret, rerollDescriptionCost }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isReady, setIsReady] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) {
      setError("Stripe is not loaded. Please refresh the page.");
      return;
    }

    const paymentElement = elements.getElement("payment");
    if (!paymentElement) {
      setError("Payment form is not ready. Please wait a moment and try again.");
      return;
    }
    
    setProcessing(true);
    setError("");

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (confirmError) {
        setError(confirmError.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      const intentResult = await stripe.retrievePaymentIntent(clientSecret);
      
      if (!intentResult.paymentIntent) {
        setError("Unable to retrieve payment information. Please try again.");
        setProcessing(false);
        return;
      }

      if (intentResult.paymentIntent.status === "succeeded") {
        onSuccess(intentResult.paymentIntent.id);
      } else {
        setError(`Payment status: ${intentResult.paymentIntent.status}. Please try again.`);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred. Please try again.");
      setProcessing(false);
      if (onError) onError(err.message || "An unexpected error occurred");
    }
  };

  return (
    <>
      <PaymentElement 
        onReady={() => {
          setIsReady(true);
        }}
        options={{
          layout: "tabs"
        }}
      />
      {!isReady && (
        <Alert variant="info" className="mt-3 brutal-alert">
          <Spinner size="sm" className="me-2" />
          LOADING PAYMENT FORM...
        </Alert>
      )}
      <div className="d-flex gap-2 mt-3">
        <Button
          className="brutal-btn brutal-btn-secondary"
          onClick={() => {
            if (onError) onError("Payment cancelled");
          }}
          disabled={processing}
        >
          CANCEL
        </Button>
        <Button
          className="brutal-btn brutal-btn-primary flex-grow-1"
          disabled={processing || !isReady}
          onClick={submit}
        >
          {processing ? (
            <>
              <Spinner size="sm" className="me-2" />
              PROCESSING...
            </>
          ) : (
            `PAY $${(rerollDescriptionCost / 100).toFixed(2)} & REROLL`
          )}
        </Button>
      </div>
      {error && (
        <Alert variant="danger" className="brutal-alert mt-2">
          {error.toUpperCase()}
        </Alert>
      )}
    </>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  
  // Display name state
  const [displayName, setDisplayName] = useState("");
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameLoading, setDisplayNameLoading] = useState(false);
  const [displayNameError, setDisplayNameError] = useState("");
  const [displayNameSuccess, setDisplayNameSuccess] = useState("");

  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, hasLength: false, hasSpecial: false });
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  // Owned days state
  const [myDays, setMyDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPersonalizedDescription, setEditPersonalizedDescription] = useState("");
  const [rerollSuggestions, setRerollSuggestions] = useState("");
  const [rerolling, setRerolling] = useState(false);
  const [rerollClientSecret, setRerollClientSecret] = useState(null);
  const [customDescriptionClientSecret, setCustomDescriptionClientSecret] = useState(null);
  const [customDescriptionPaymentLoading, setCustomDescriptionPaymentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  // Coupon management state (Admin only)
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minPurchaseAmount: "",
    maxDiscountAmount: "",
    validFrom: "",
    validUntil: "",
    usageLimit: "",
    applicableTo: "all",
    isActive: true,
  });
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  
  // Pricing state
  const [customDescriptionCost, setCustomDescriptionCost] = useState(500); // Default $5.00
  const [rerollDescriptionCost, setRerollDescriptionCost] = useState(100); // Default $1.00

  // Fetch Stripe publishable key and pricing from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch pricing
        const pricingRes = await fetch("/api/pricing");
        if (pricingRes.ok) {
          const pricingData = await pricingRes.json();
          setCustomDescriptionCost(pricingData.customDescriptionCost || 500);
          setRerollDescriptionCost(pricingData.rerollDescriptionCost || 100);
        }

        // Fetch Stripe publishable key
        const res = await fetch("/api/stripe-publishable-key");
        const data = await res.json();
        
        if (res.ok && data.publishableKey) {
          const promise = loadStripe(data.publishableKey);
          promise.catch((error) => {
            console.error("Failed to load Stripe.js:", error);
          });
          setStripePromise(promise);
        } else {
          console.error("Failed to retrieve Stripe configuration");
        }
      } catch (error) {
        console.error("Error fetching configuration:", error);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchMyDays();
  }, []);

  useEffect(() => {
    if (user && user.role === "ADMIN") {
      fetchCoupons();
    }
    if (user && user.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  const fetchMyDays = async () => {
    try {
      setLoadingDays(true);
      const token = localStorage.getItem("token");
      const res = await fetch("/api/days/my-days", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error("Failed to fetch your days");
      }
      const data = await res.json();
      setMyDays(data);
      return data; // Return the data so it can be used by callers
    } catch (err) {
      console.error("Error fetching owned days:", err);
      return [];
    } finally {
      setLoadingDays(false);
    }
  };

  // Check if a date has passed (is in the past)
  const isDateInPast = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateObj = new Date(dateString + "T00:00:00");
    dateObj.setHours(0, 0, 0, 0);
    return dateObj < today;
  };

  const handleEditDay = (day) => {
    setEditingDay(day);
    setEditTitle(day.title || "");
    setEditDescription(day.description || "");
    setEditPersonalizedDescription(day.personalizedDescription || "");
    setRerollSuggestions("");
    setRerollClientSecret(null);
    setCustomDescriptionClientSecret(null);
    setSaveError("");
    setSaveSuccess("");
  };

  // Start reroll payment
  const startRerollPayment = async () => {
    if (!editingDay) return;

    // Validate that the date has passed
    if (!isDateInPast(editingDay.date)) {
      setSaveError("Reroll is only available for dates that have passed. Please wait until this day has passed.");
      return;
    }

    setRerolling(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/openai/reroll/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dayId: editingDay._id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.clientSecret) {
        setRerollClientSecret(data.clientSecret);
      } else {
        setSaveError(data.message || "Failed to start payment");
      }
    } catch (err) {
      console.error("Error starting reroll payment:", err);
      setSaveError("An error occurred. Please try again.");
    } finally {
      setRerolling(false);
    }
  };

  // Confirm reroll after payment
  const confirmReroll = async (paymentIntentId) => {
    if (!editingDay) return;

    setRerolling(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/openai/regenerate-description", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dayId: editingDay._id,
          date: editingDay.date,
          suggestions: rerollSuggestions,
          paymentIntentId: paymentIntentId,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setEditDescription(data.description);
        setSaveSuccess("Description regenerated successfully!");
        setRerollClientSecret(null);
        setRerollSuggestions("");
        // Update the day in the list
        setMyDays(myDays.map((d) => 
          d._id === editingDay._id 
            ? { ...d, description: data.description }
            : d
        ));
        setTimeout(() => setSaveSuccess(""), 3000);
      } else {
        setSaveError(data.message || "Failed to regenerate description");
      }
    } catch (err) {
      console.error("Error regenerating description:", err);
      setSaveError("An error occurred. Please try again.");
    } finally {
      setRerolling(false);
    }
  };

  // Start custom description payment
  const startCustomDescriptionPayment = async () => {
    if (!editingDay) return;

    setCustomDescriptionPaymentLoading(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/days/custom-description/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dayId: editingDay._id,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.alreadyPaid) {
          setSaveSuccess("Custom description editing is already unlocked!");
          setTimeout(() => setSaveSuccess(""), 3000);
          // Refresh the day data to get the updated status
          fetchMyDays();
        } else if (data.clientSecret) {
          setCustomDescriptionClientSecret(data.clientSecret);
        }
      } else {
        setSaveError(data.message || "Failed to start payment");
      }
    } catch (err) {
      console.error("Error starting custom description payment:", err);
      setSaveError("An error occurred. Please try again.");
    } finally {
      setCustomDescriptionPaymentLoading(false);
    }
  };

  // Confirm custom description payment
  const confirmCustomDescriptionPayment = async (paymentIntentId) => {
    if (!editingDay) return;

    setCustomDescriptionPaymentLoading(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/days/custom-description/confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dayId: editingDay._id,
          paymentIntentId: paymentIntentId,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSaveSuccess("Custom description editing unlocked! You can now edit your description.");
        setCustomDescriptionClientSecret(null);
        // Refresh the day data and update editingDay
        const updatedDays = await fetchMyDays();
        // Update editingDay with the refreshed data
        const updatedDay = updatedDays.find((d) => d._id === editingDay._id);
        if (updatedDay) {
          setEditingDay(updatedDay);
        }
        setTimeout(() => setSaveSuccess(""), 3000);
      } else {
        setSaveError(data.message || "Failed to confirm payment");
      }
    } catch (err) {
      console.error("Error confirming custom description payment:", err);
      setSaveError("An error occurred. Please try again.");
    } finally {
      setCustomDescriptionPaymentLoading(false);
    }
  };

  const handleSaveDay = async () => {
    if (!editingDay) return;

    setSaving(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/days/${editingDay._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription, // Keep current description (read-only, but still send it)
          personalizedDescription: editPersonalizedDescription,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setSaveSuccess("Day updated successfully!");
        // Update the day in the list
        setMyDays(myDays.map((d) => 
          d._id === editingDay._id 
            ? { ...d, title: editTitle, description: editDescription, personalizedDescription: editPersonalizedDescription }
            : d
        ));
        setTimeout(() => {
          setEditingDay(null);
          setSaveSuccess("");
        }, 1500);
      } else {
        setSaveError(data.message || "Failed to update day");
      }
    } catch (err) {
      setSaveError("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await res.json();
      setUser(data);
      if (data.displayName) {
        setDisplayName(data.displayName);
      }
    } catch (err) {
      setError("Failed to load profile information");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDisplayName = async (e) => {
    e.preventDefault();
    setDisplayNameError("");
    setDisplayNameSuccess("");
    setDisplayNameLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/auth/update-display-name", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ displayName: displayName.trim() }),
      });

      const data = await res.json();

      if (res.ok) {
        setDisplayNameSuccess("Display name updated successfully!");
        setEditingDisplayName(false);
        // Refresh user data
        fetchUserData();
        setTimeout(() => setDisplayNameSuccess(""), 3000);
      } else {
        console.error("Display name update failed:", data);
        setDisplayNameError(data.message || "Failed to update display name");
      }
    } catch (err) {
      console.error("Error updating display name:", err);
      setDisplayNameError("An error occurred. Please try again.");
    } finally {
      setDisplayNameLoading(false);
    }
  };

  // Coupon management functions (Admin only)
  const fetchCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const token = localStorage.getItem("token");
      const res = await fetch("/api/coupons", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch coupons");
      const data = await res.json();
      setCoupons(data);
    } catch (err) {
      setCouponError("Failed to load coupons");
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleOpenCouponModal = (coupon = null) => {
    // Only allow admins to open coupon modal
    if (!user || user.role !== "ADMIN") return;
    
    if (coupon) {
      setEditingCoupon(coupon);
      setCouponForm({
        code: coupon.code,
        description: coupon.description || "",
        discountType: coupon.discountType,
        discountValue: coupon.discountValue.toString(),
        minPurchaseAmount: (coupon.minPurchaseAmount || 0).toString(),
        maxDiscountAmount: (coupon.maxDiscountAmount || "").toString(),
        validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split('T')[0] : "",
        validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().split('T')[0] : "",
        usageLimit: (coupon.usageLimit || "").toString(),
        applicableTo: coupon.applicableTo || "all",
        isActive: coupon.isActive,
      });
    } else {
      setEditingCoupon(null);
      setCouponForm({
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        minPurchaseAmount: "",
        maxDiscountAmount: "",
        validFrom: "",
        validUntil: "",
        usageLimit: "",
        applicableTo: "all",
        isActive: true,
      });
    }
    setShowCouponModal(true);
    setCouponError("");
    setCouponSuccess("");
  };

  const handleSaveCoupon = async (e) => {
    e.preventDefault();
    
    // Only allow admins to save coupons
    if (!user || user.role !== "ADMIN") {
      setCouponError("Unauthorized access");
      return;
    }
    
    setCouponError("");
    setCouponSuccess("");
    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...couponForm,
        discountValue: parseFloat(couponForm.discountValue),
        minPurchaseAmount: couponForm.minPurchaseAmount ? parseFloat(couponForm.minPurchaseAmount) : 0,
        maxDiscountAmount: couponForm.maxDiscountAmount ? parseFloat(couponForm.maxDiscountAmount) : null,
        validFrom: couponForm.validFrom || new Date().toISOString().split('T')[0],
        validUntil: couponForm.validUntil,
        usageLimit: couponForm.usageLimit ? parseInt(couponForm.usageLimit) : null,
      };

      const url = editingCoupon ? `/api/coupons/${editingCoupon._id}` : "/api/coupons";
      const method = editingCoupon ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setCouponSuccess(editingCoupon ? "Coupon updated successfully!" : "Coupon created successfully!");
        fetchCoupons();
        setTimeout(() => {
          setShowCouponModal(false);
          setCouponSuccess("");
        }, 1500);
      } else {
        setCouponError(data.message || "Failed to save coupon");
      }
    } catch (err) {
      setCouponError("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    // Only allow admins to delete coupons
    if (!user || user.role !== "ADMIN") return;
    
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/coupons/${couponId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setCouponSuccess("Coupon deleted successfully!");
        fetchCoupons();
        setTimeout(() => setCouponSuccess(""), 2000);
      } else {
        const data = await res.json();
        setCouponError(data.message || "Failed to delete coupon");
      }
    } catch (err) {
      setCouponError("An error occurred. Please try again.");
    }
  };

  const handleToggleCouponActive = async (coupon) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/coupons/${coupon._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: !coupon.isActive }),
      });

      if (res.ok) {
        fetchCoupons();
      } else {
        const data = await res.json();
        setCouponError(data.message || "Failed to update coupon");
      }
    } catch (err) {
      setCouponError("An error occurred. Please try again.");
    }
  };

  const generateCertificate = async (day) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/pdf/${day}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        let errorMessage = "Failed to generate PDF";
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `TimeRegistry-Certificate-${day}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Failed to generate certificate");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordLoading(true);

    const currentPassword = e.target.currentPassword.value;
    const newPassword = e.target.newPassword.value;
    const confirmPassword = e.target.confirmPassword.value;

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      setPasswordLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      setPasswordLoading(false);
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      setPasswordLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setPasswordSuccess("Password changed successfully!");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordValidation({ isValid: false, hasLength: false, hasSpecial: false });
        setPasswordsMatch(false);
        e.target.reset(); // Clear form
        setTimeout(() => {
          setPasswordSuccess("");
        }, 3000);
      } else {
        setPasswordError(data.message || "Failed to change password");
      }
    } catch (err) {
      setPasswordError("An error occurred. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3 text-muted">Loading profile...</p>
        </div>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate("/dashboard")} className="mt-3">
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <>
    <Container className="mt-5 px-3 px-md-4 profile-container" style={{ maxWidth: 700 }}>
      <style>{`
        @media (max-width: 768px) {
          .profile-container {
            padding-left: 8px !important;
            padding-right: 8px !important;
            margin-top: 1rem !important;
          }
          .profile-card-title {
            font-size: 24px !important;
            margin-bottom: 16px !important;
          }
          .profile-card-body {
            padding: 16px !important;
          }
          .profile-display-name-form .d-flex {
            flex-direction: column;
            gap: 8px !important;
          }
          .profile-display-name-form .brutal-form-control {
            width: 100%;
          }
          .profile-display-name-form .brutal-btn {
            width: 100%;
            min-height: 44px;
          }
          .profile-day-card {
            padding: 12px !important;
          }
          .profile-day-card .d-flex {
            flex-direction: column;
            gap: 12px;
          }
          .profile-day-card .d-flex.gap-2 {
            flex-direction: row;
            width: 100%;
            justify-content: stretch;
          }
          .profile-day-card .brutal-btn {
            flex: 1;
            min-height: 44px;
          }
          .profile-coupon-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 12px;
          }
          .profile-coupon-header .brutal-btn {
            width: 100%;
            min-height: 44px;
          }
          .profile-coupon-card .d-flex.gap-2 {
            flex-direction: column;
            width: 100%;
          }
          .profile-coupon-card .brutal-btn {
            width: 100%;
            min-height: 44px;
          }
          .profile-coupon-info {
            min-width: 100% !important;
            margin-bottom: 12px;
          }
          .profile-password-form .position-relative {
            margin-bottom: 8px;
          }
          .profile-password-form .brutal-form-control {
            padding-right: 45px;
          }
          .profile-password-form .brutal-subheading {
            font-size: 14px;
            margin-bottom: 8px;
          }
          .profile-password-validation {
            font-size: 11px;
            margin-top: 6px;
          }
          .profile-password-validation div {
            margin-bottom: 4px;
          }
          .brutal-subheading {
            font-size: 16px;
          }
          .brutal-text {
            font-size: 13px;
          }
        }
        @media (max-width: 575px) {
          .profile-container {
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          .profile-card-title {
            font-size: 20px !important;
          }
          .profile-card-body {
            padding: 12px !important;
          }
          .profile-day-card {
            padding: 10px !important;
          }
          .brutal-subheading {
            font-size: 14px;
          }
          .brutal-text {
            font-size: 12px;
          }
        }
      `}</style>
      {/* User Information Card */}
      <Card className="brutal-card mb-4">
        <Card.Body className="p-4">
          <Card.Title className="brutal-heading mb-4 profile-card-title" style={{ fontSize: "32px" }}>PROFILE</Card.Title>
            
            {user && (
              <div>
                <div className="mb-4">
                  <div className="brutal-subheading mb-2">DISPLAY NAME</div>
                  {editingDisplayName ? (
                    <Form onSubmit={handleUpdateDisplayName} className="profile-display-name-form">
                      {displayNameError && <Alert variant="danger" className="brutal-alert mb-3">{displayNameError.toUpperCase()}</Alert>}
                      {displayNameSuccess && <Alert variant="success" className="brutal-alert mb-3">{displayNameSuccess.toUpperCase()}</Alert>}
                      <div className="d-flex gap-2 mb-2">
                        <Form.Control
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          placeholder="DISPLAY NAME (MAX 30 CHARS)"
                          className="brutal-form-control"
                          maxLength={30}
                          disabled={displayNameLoading}
                          required
                        />
                        <Button
                          type="submit"
                          className="brutal-btn brutal-btn-primary"
                          disabled={displayNameLoading}
                        >
                          {displayNameLoading ? <Spinner size="sm" /> : "SAVE"}
                        </Button>
                        <Button
                          type="button"
                          className="brutal-btn brutal-btn-secondary"
                          onClick={() => {
                            setEditingDisplayName(false);
                            setDisplayName(user.displayName || "");
                            setDisplayNameError("");
                            setDisplayNameSuccess("");
                          }}
                          disabled={displayNameLoading}
                        >
                          CANCEL
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="brutal-text" style={{ textTransform: "none", fontWeight: "400" }}>
                        {user.displayName || "Not set"}
                      </div>
                      <Button
                        className="brutal-btn brutal-btn-secondary"
                        size="sm"
                        onClick={() => setEditingDisplayName(true)}
                      >
                        EDIT
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="brutal-subheading mb-2">EMAIL</div>
                  <div className="brutal-text" style={{ textTransform: "none", fontWeight: "400" }}>{user.email}</div>
                </div>
                
                <div className="mb-4">
                  <div className="brutal-subheading mb-2">ROLE</div>
                  <Badge className={`brutal-badge ${user.role === "ADMIN" ? "brutal-badge-danger" : "brutal-badge-primary"}`}>
                    {user.role}
                  </Badge>
                </div>

                <div className="mb-4">
                  <div className="brutal-subheading mb-2">ACCOUNT STATUS</div>
                  <Badge className={`brutal-badge ${user.isVerified ? "brutal-badge-success" : "brutal-badge-warning"}`}>
                    {user.isVerified ? "VERIFIED" : "NOT VERIFIED"}
                  </Badge>
                </div>

                {user.createdAt && (
                  <div className="mb-4">
                    <div className="brutal-subheading mb-2">MEMBER SINCE</div>
                    <div className="brutal-text" style={{ textTransform: "none", fontWeight: "400" }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* My Purchased Days Card */}
        <Card className="brutal-card mb-4">
          <Card.Body className="p-4 profile-card-body">
            <Card.Title className="brutal-heading mb-4 profile-card-title" style={{ fontSize: "28px" }}>MY PURCHASED DAYS</Card.Title>

            {loadingDays ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : myDays.length === 0 ? (
              <p className="brutal-text text-center py-3">
                YOU HAVEN'T PURCHASED ANY DAYS YET.
              </p>
            ) : (
              <div>
                {myDays.map((day) => (
                  <Card key={day._id} className="brutal-card mb-3">
                    <Card.Body className="p-3 profile-day-card">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="brutal-subheading mb-2">{day.date}</h6>
                          {day.title && (
                            <p className="brutal-text mb-2" style={{ fontSize: "16px", color: "#0000FF" }}>{day.title}</p>
                          )}
                          {day.description && (
                            <p className="brutal-text mb-2" style={{ fontSize: "12px", textTransform: "none", fontWeight: "400" }}>{day.description}</p>
                          )}
                          {!day.title && !day.description && (
                            <p className="brutal-text mb-0" style={{ fontSize: "12px" }}>NO TITLE OR DESCRIPTION SET</p>
                          )}
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            className="brutal-btn brutal-btn-success"
                            size="sm"
                            onClick={() => generateCertificate(day.date)}
                          >
                            PDF
                          </Button>
                          <Button
                            className="brutal-btn brutal-btn-secondary"
                            size="sm"
                            onClick={() => handleEditDay(day)}
                          >
                            EDIT
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Coupon Management Card - Admin Only */}
        {user && user.role === "ADMIN" && (
          <Card className="brutal-card mb-4">
            <Card.Body className="p-4 profile-card-body">
              <div className="d-flex justify-content-between align-items-center mb-4 profile-coupon-header">
                <Card.Title className="brutal-heading mb-0 profile-card-title" style={{ fontSize: "28px" }}>COUPON MANAGEMENT</Card.Title>
                <Button
                  className="brutal-btn brutal-btn-primary"
                  onClick={() => handleOpenCouponModal()}
                >
                  + CREATE COUPON
                </Button>
              </div>

              {couponError && <Alert variant="danger" className="brutal-alert mb-3">{couponError.toUpperCase()}</Alert>}
              {couponSuccess && <Alert variant="success" className="brutal-alert mb-3">{couponSuccess.toUpperCase()}</Alert>}

              {loadingCoupons ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : coupons.length === 0 ? (
                <p className="brutal-text text-center py-3">NO COUPONS CREATED YET.</p>
              ) : (
                <div>
                  {coupons.map((coupon) => (
                    <Card key={coupon._id} className="brutal-card mb-3">
                      <Card.Body className="p-3 profile-coupon-card">
                        <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-3">
                          <div className="flex-grow-1 profile-coupon-info" style={{ minWidth: "200px" }}>
                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                              <h6 className="brutal-subheading mb-0">{coupon.code}</h6>
                              <Badge className={`brutal-badge ${coupon.isActive ? "brutal-badge-success" : "brutal-badge-warning"}`}>
                                {coupon.isActive ? "ACTIVE" : "INACTIVE"}
                              </Badge>
                              {coupon.usageLimit && (
                                <Badge className="brutal-badge brutal-badge-primary">
                                  {coupon.usageCount}/{coupon.usageLimit} USED
                                </Badge>
                              )}
                            </div>
                            {coupon.description && (
                              <p className="brutal-text mb-2" style={{ fontSize: "12px", textTransform: "none", fontWeight: "400" }}>
                                {coupon.description}
                              </p>
                            )}
                            <div className="brutal-text" style={{ fontSize: "12px" }}>
                              <div>DISCOUNT: {coupon.discountType === "percentage" ? `${coupon.discountValue}%` : `$${coupon.discountValue / 100}`}</div>
                              <div>VALID UNTIL: {new Date(coupon.validUntil).toLocaleDateString()}</div>
                              <div>APPLICABLE TO: {coupon.applicableTo.toUpperCase().replace("_", " ")}</div>
                            </div>
                          </div>
                          <div className="d-flex gap-2 flex-wrap" style={{ minWidth: "fit-content" }}>
                            <Button
                              className="brutal-btn brutal-btn-secondary"
                              size="sm"
                              onClick={() => handleToggleCouponActive(coupon)}
                            >
                              {coupon.isActive ? "DEACTIVATE" : "ACTIVATE"}
                            </Button>
                            <Button
                              className="brutal-btn brutal-btn-primary"
                              size="sm"
                              onClick={() => handleOpenCouponModal(coupon)}
                            >
                              EDIT
                            </Button>
                            <Button
                              className="brutal-btn brutal-btn-danger"
                              size="sm"
                              onClick={() => handleDeleteCoupon(coupon._id)}
                            >
                              DELETE
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Change Password Card */}
        <Card className="brutal-card" style={{ marginBottom: "30px" }}>
          <Card.Body className="p-4 profile-card-body">
            <Card.Title className="brutal-heading mb-4 profile-card-title" style={{ fontSize: "28px" }}>CHANGE PASSWORD</Card.Title>

            {passwordError && <Alert variant="danger" className="brutal-alert mb-4">{passwordError.toUpperCase()}</Alert>}
            {passwordSuccess && <Alert variant="success" className="brutal-alert mb-4">{passwordSuccess.toUpperCase()}</Alert>}

            <Form onSubmit={handlePasswordChange} className="profile-password-form">
              <Form.Group className="mb-3">
                <Form.Label className="brutal-subheading mb-2 profile-password-label">CURRENT PASSWORD</Form.Label>
                <Form.Control
                  name="currentPassword"
                  type="password"
                  placeholder="CURRENT PASSWORD"
                  className="brutal-form-control"
                  required
                  disabled={passwordLoading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="brutal-subheading mb-2 profile-password-label">NEW PASSWORD</Form.Label>
                <div className="position-relative profile-password-input-wrapper">
                  <Form.Control
                    name="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      const pwd = e.target.value;
                      setNewPassword(pwd);
                      const validation = validatePassword(pwd);
                      setPasswordValidation({
                        isValid: validation.isValid,
                        hasLength: pwd.length >= 8,
                        hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
                      });
                      if (confirmPassword) {
                        setPasswordsMatch(pwd === confirmPassword);
                      }
                    }}
                    placeholder="ENTER NEW PASSWORD (MIN 8 CHARS, 1 SPECIAL CHAR)"
                    className="brutal-form-control"
                    required
                    minLength={8}
                    disabled={passwordLoading}
                    style={{
                      borderColor: newPassword ? (passwordValidation.isValid ? "#00FF00" : "#FF0000") : undefined,
                      borderWidth: newPassword ? "3px" : undefined,
                    }}
                  />
                  {newPassword && (
                    <span
                      style={{
                        position: "absolute",
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "20px",
                        fontWeight: "900",
                      }}
                    >
                      {passwordValidation.isValid ? "✓" : "✗"}
                    </span>
                  )}
                </div>
                {newPassword && (
                  <div className="mt-2 profile-password-validation" style={{ fontSize: "12px" }}>
                    <div style={{ color: passwordValidation.hasLength ? "#00FF00" : "#FF0000", fontWeight: "700" }}>
                      {passwordValidation.hasLength ? "✓" : "✗"} AT LEAST 8 CHARACTERS
                    </div>
                    <div style={{ color: passwordValidation.hasSpecial ? "#00FF00" : "#FF0000", fontWeight: "700" }}>
                      {passwordValidation.hasSpecial ? "✓" : "✗"} AT LEAST 1 SPECIAL CHARACTER
                    </div>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="brutal-subheading mb-2 profile-password-label">CONFIRM NEW PASSWORD</Form.Label>
                <div className="position-relative profile-password-input-wrapper">
                  <Form.Control
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      const pwd = e.target.value;
                      setConfirmPassword(pwd);
                      setPasswordsMatch(newPassword && pwd === newPassword);
                    }}
                    placeholder="CONFIRM NEW PASSWORD"
                    className="brutal-form-control"
                    required
                    minLength={8}
                    disabled={passwordLoading}
                    style={{
                      borderColor: confirmPassword ? (passwordsMatch ? "#00FF00" : "#FF0000") : undefined,
                      borderWidth: confirmPassword ? "3px" : undefined,
                    }}
                  />
                  {confirmPassword && (
                    <span
                      style={{
                        position: "absolute",
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "20px",
                        fontWeight: "900",
                      }}
                    >
                      {passwordsMatch ? "✓" : "✗"}
                    </span>
                  )}
                </div>
                {confirmPassword && (
                  <div className="mt-2 profile-password-validation" style={{ fontSize: "12px", fontWeight: "700", color: passwordsMatch ? "#00FF00" : "#FF0000" }}>
                    {passwordsMatch ? "✓ PASSWORDS MATCH" : "✗ PASSWORDS DO NOT MATCH"}
                  </div>
                )}
              </Form.Group>

              <Button
                type="submit"
                className="brutal-btn brutal-btn-primary w-100"
                disabled={passwordLoading || !passwordValidation.isValid || !passwordsMatch || !newPassword || !confirmPassword}
              >
                {passwordLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    CHANGING PASSWORD...
                  </>
                ) : (
                  "CHANGE PASSWORD"
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {/* Edit Day Modal */}
        <Modal show={editingDay !== null} onHide={() => setEditingDay(null)} className="brutal-modal">
          <Modal.Header closeButton className="brutal-modal-header">
            <Modal.Title className="brutal-heading" style={{ fontSize: "24px" }}>EDIT DAY: {editingDay?.date}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="brutal-modal-body">
            {saveError && <Alert variant="danger" className="brutal-alert mb-3">{saveError.toUpperCase()}</Alert>}
            {saveSuccess && <Alert variant="success" className="brutal-alert mb-3">{saveSuccess.toUpperCase()}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label className="brutal-subheading mb-2">TITLE</Form.Label>
              <Form.Control
                type="text"
                value={editTitle}
                onChange={(e) => {
                  if (e.target.value.length <= 40) {
                    setEditTitle(e.target.value);
                  }
                }}
                placeholder="ENTER A CATCHY TITLE (MAX 40 CHARACTERS)"
                className="brutal-form-control"
                disabled={saving}
                maxLength={40}
              />
              <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                {editTitle.length}/40 characters
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="brutal-subheading mb-2">DESCRIPTION</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editDescription}
                readOnly
                className="brutal-form-control"
                style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
              />
              <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                Description is auto-generated. Use the reroll feature below to regenerate it.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="brutal-subheading mb-2">REROLL SUGGESTIONS</Form.Label>
              {isDateInPast(editingDay?.date) ? (
                <>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={rerollSuggestions}
                    onChange={(e) => setRerollSuggestions(e.target.value)}
                    placeholder="ENTER SUGGESTIONS FOR REGENERATING THE DESCRIPTION (e.g., 'Focus on scientific discoveries' or 'Emphasize cultural events')"
                    className="brutal-form-control"
                    disabled={rerolling || saving || rerollClientSecret}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                    Enter suggestions to guide the AI in regenerating the description. Leave empty for a fresh generation. Reroll costs ${(rerollDescriptionCost / 100).toFixed(2)}.
                  </Form.Text>
                  {!rerollClientSecret && (
                    <Button
                      className="brutal-btn brutal-btn-primary w-100 mt-2"
                      onClick={startRerollPayment}
                      disabled={rerolling || saving}
                    >
                      {rerolling ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          STARTING PAYMENT...
                        </>
                      ) : (
                        `PAY $${(rerollDescriptionCost / 100).toFixed(2)} TO REROLL`
                      )}
                    </Button>
                  )}
                </>
              ) : (
                <Alert variant="info" className="brutal-alert mb-0">
                  REROLL IS ONLY AVAILABLE FOR DATES THAT HAVE PASSED. 
                  <br />
                  <div className="brutal-text" style={{ fontSize: "11px", marginTop: "8px" }}>
                    ONCE THIS DAY HAS PASSED, YOU WILL BE ABLE TO REROLL THE DESCRIPTION.
                  </div>
                </Alert>
              )}
            </Form.Group>

            {rerollClientSecret && stripePromise && (
              <Form.Group className="mb-3">
                <Form.Label className="brutal-subheading mb-2">PAYMENT (${(rerollDescriptionCost / 100).toFixed(2)})</Form.Label>
                <Elements stripe={stripePromise} options={{ clientSecret: rerollClientSecret }}>
                  <RerollCheckout
                    onSuccess={(paymentIntentId) => {
                      confirmReroll(paymentIntentId);
                    }}
                    onError={(errorMsg) => {
                      setSaveError(errorMsg || "Payment failed. Please try again.");
                      setRerollClientSecret(null);
                    }}
                    clientSecret={rerollClientSecret}
                    rerollDescriptionCost={rerollDescriptionCost}
                  />
                </Elements>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="brutal-subheading mb-2">PERSONALIZED DESCRIPTION</Form.Label>
              {editingDay?.customDescriptionPaid ? (
                <>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editPersonalizedDescription}
                    onChange={(e) => setEditPersonalizedDescription(e.target.value)}
                    placeholder="ENTER YOUR PERSONALIZED DESCRIPTION"
                    className="brutal-form-control"
                    disabled={saving}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                    PERSONAL NOTE FOR YOUR RECORDS — NOT SHOWN ON THE REGISTRY, PUBLIC BROWSE, OR TO OTHER USERS. EDITING IS UNLOCKED.
                  </Form.Text>
                </>
              ) : (
                <>
                  {!customDescriptionClientSecret && (
                    <>
                      <Alert variant="info" className="brutal-alert mb-3">
                        PAY ${(customDescriptionCost / 100).toFixed(2)} TO UNLOCK CUSTOM DESCRIPTION EDITING. ONCE UNLOCKED, YOU CAN EDIT AS MANY TIMES AS YOU WANT.
                      </Alert>
                      <Button
                        className="brutal-btn brutal-btn-primary w-100"
                        onClick={startCustomDescriptionPayment}
                        disabled={customDescriptionPaymentLoading || saving}
                      >
                        {customDescriptionPaymentLoading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            STARTING PAYMENT...
                          </>
                        ) : (
                          `PAY $${(customDescriptionCost / 100).toFixed(2)} TO UNLOCK`
                        )}
                      </Button>
                    </>
                  )}
                  {customDescriptionClientSecret && stripePromise && (
                    <Elements stripe={stripePromise} options={{ clientSecret: customDescriptionClientSecret }}>
                      <CustomDescriptionCheckout
                        onSuccess={(paymentIntentId) => {
                          confirmCustomDescriptionPayment(paymentIntentId);
                        }}
                        onError={(errorMsg) => {
                          setSaveError(errorMsg || "Payment failed. Please try again.");
                          setCustomDescriptionClientSecret(null);
                        }}
                        clientSecret={customDescriptionClientSecret}
                        customDescriptionCost={customDescriptionCost}
                      />
                    </Elements>
                  )}
                </>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="brutal-modal-footer">
            <Button
              className="brutal-btn brutal-btn-secondary"
              onClick={() => setEditingDay(null)}
              disabled={saving}
            >
              CANCEL
            </Button>
            <Button
              className="brutal-btn brutal-btn-primary"
              onClick={handleSaveDay}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  SAVING...
                </>
              ) : (
                "SAVE CHANGES"
              )}
            </Button>
          </Modal.Footer>
          <style>{`
            .brutal-modal .modal-content {
              border: 6px solid #000000;
              border-radius: 0;
              box-shadow: 12px 12px 0px #000000;
            }
            .brutal-modal-header {
              border-bottom: 4px solid #000000;
              background: #FFFFFF;
            }
            .brutal-modal-body {
              background: #FFFFFF;
            }
            .brutal-modal-footer {
              border-top: 4px solid #000000;
              background: #FFFFFF;
            }
            .modal-backdrop {
              background: rgba(0, 0, 0, 0.8);
            }
          `}</style>
        </Modal>

        {/* Coupon Management Modal - Admin Only */}
        {user && user.role === "ADMIN" && (
        <Modal show={showCouponModal} onHide={() => setShowCouponModal(false)} className="brutal-modal">
          <Modal.Header closeButton className="brutal-modal-header">
            <Modal.Title className="brutal-heading" style={{ fontSize: "24px" }}>
              {editingCoupon ? "EDIT COUPON" : "CREATE COUPON"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="brutal-modal-body">
            {couponError && <Alert variant="danger" className="brutal-alert mb-3">{couponError.toUpperCase()}</Alert>}
            {couponSuccess && <Alert variant="success" className="brutal-alert mb-3">{couponSuccess.toUpperCase()}</Alert>}

            <Form onSubmit={handleSaveCoupon}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">CODE *</Form.Label>
                    <Form.Control
                      type="text"
                      value={couponForm.code}
                      onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                      placeholder="COUPON CODE"
                      className="brutal-form-control"
                      required
                      disabled={!!editingCoupon || saving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">DESCRIPTION</Form.Label>
                    <Form.Control
                      type="text"
                      value={couponForm.description}
                      onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                      placeholder="DESCRIPTION"
                      className="brutal-form-control"
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">DISCOUNT TYPE *</Form.Label>
                    <Form.Select
                      value={couponForm.discountType}
                      onChange={(e) => setCouponForm({ ...couponForm, discountType: e.target.value })}
                      className="brutal-form-control"
                      required
                      disabled={saving}
                    >
                      <option value="percentage">PERCENTAGE</option>
                      <option value="fixed">FIXED AMOUNT</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">DISCOUNT VALUE *</Form.Label>
                    <Form.Control
                      type="number"
                      value={couponForm.discountValue}
                      onChange={(e) => setCouponForm({ ...couponForm, discountValue: e.target.value })}
                      placeholder={couponForm.discountType === "percentage" ? "PERCENTAGE (0-100)" : "AMOUNT IN CENTS"}
                      className="brutal-form-control"
                      required
                      min="0"
                      max={couponForm.discountType === "percentage" ? "100" : undefined}
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">MIN PURCHASE AMOUNT (CENTS)</Form.Label>
                    <Form.Control
                      type="number"
                      value={couponForm.minPurchaseAmount}
                      onChange={(e) => setCouponForm({ ...couponForm, minPurchaseAmount: e.target.value })}
                      placeholder="0"
                      className="brutal-form-control"
                      min="0"
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">MAX DISCOUNT (CENTS)</Form.Label>
                    <Form.Control
                      type="number"
                      value={couponForm.maxDiscountAmount}
                      onChange={(e) => setCouponForm({ ...couponForm, maxDiscountAmount: e.target.value })}
                      placeholder="NO LIMIT"
                      className="brutal-form-control"
                      min="0"
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">VALID FROM</Form.Label>
                    <Form.Control
                      type="date"
                      value={couponForm.validFrom}
                      onChange={(e) => setCouponForm({ ...couponForm, validFrom: e.target.value })}
                      className="brutal-form-control"
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">VALID UNTIL *</Form.Label>
                    <Form.Control
                      type="date"
                      value={couponForm.validUntil}
                      onChange={(e) => setCouponForm({ ...couponForm, validUntil: e.target.value })}
                      className="brutal-form-control"
                      required
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">USAGE LIMIT</Form.Label>
                    <Form.Control
                      type="number"
                      value={couponForm.usageLimit}
                      onChange={(e) => setCouponForm({ ...couponForm, usageLimit: e.target.value })}
                      placeholder="UNLIMITED"
                      className="brutal-form-control"
                      min="1"
                      disabled={saving}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="brutal-subheading mb-2">APPLICABLE TO</Form.Label>
                    <Form.Select
                      value={couponForm.applicableTo}
                      onChange={(e) => setCouponForm({ ...couponForm, applicableTo: e.target.value })}
                      className="brutal-form-control"
                      disabled={saving}
                    >
                      <option value="all">ALL</option>
                      <option value="new_purchases">NEW PURCHASES</option>
                      <option value="marketplace">MARKETPLACE</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="ACTIVE"
                  checked={couponForm.isActive}
                  onChange={(e) => setCouponForm({ ...couponForm, isActive: e.target.checked })}
                  disabled={saving}
                  className="brutal-text"
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  type="button"
                  className="brutal-btn brutal-btn-secondary"
                  onClick={() => setShowCouponModal(false)}
                  disabled={saving}
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  className="brutal-btn brutal-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      SAVING...
                    </>
                  ) : (
                    editingCoupon ? "UPDATE COUPON" : "CREATE COUPON"
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
          <style>{`
            .brutal-modal .modal-content {
              border: 6px solid #000000;
              border-radius: 0;
              box-shadow: 12px 12px 0px #000000;
              background: #FFFFFF;
            }
            .brutal-modal-header {
              border-bottom: 4px solid #000000;
              background: #FFFFFF;
              padding: 20px 30px;
            }
            .brutal-modal-header .btn-close {
              filter: invert(1);
              opacity: 1;
              border: 3px solid #000000;
              padding: 8px;
            }
            .brutal-modal-body {
              background: #FFFFFF;
              padding: 30px;
            }
            .brutal-modal-footer {
              border-top: 4px solid #000000;
              background: #FFFFFF;
              padding: 20px 30px;
            }
            .modal-backdrop {
              background: rgba(0, 0, 0, 0.8);
            }
            .brutal-modal .form-select {
              border: 4px solid #000000;
              border-radius: 0;
              padding: 12px 16px;
              font-weight: 700;
              font-size: 14px;
              text-transform: uppercase;
              letter-spacing: 1px;
              background: #FFFFFF;
              color: #000000;
              box-shadow: 4px 4px 0px #000000;
            }
            .brutal-modal .form-select:focus {
              border-color: #000000;
              box-shadow: 4px 4px 0px #000000, 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
              outline: none;
              background: #FFFF00;
            }
            .brutal-modal .form-check-input {
              width: 24px;
              height: 24px;
              border: 4px solid #000000;
              border-radius: 0;
              box-shadow: 3px 3px 0px #000000;
              cursor: pointer;
            }
            .brutal-modal .form-check-input:checked {
              background-color: #000000;
              border-color: #000000;
            }
            .brutal-modal .form-check-input:focus {
              box-shadow: 3px 3px 0px #000000, 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
            }
            .brutal-modal .form-check-label {
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-left: 10px;
              cursor: pointer;
            }
            .brutal-modal .row {
              margin-left: -10px;
              margin-right: -10px;
            }
            .brutal-modal .row > * {
              padding-left: 10px;
              padding-right: 10px;
            }
            @media (max-width: 768px) {
              .brutal-modal .modal-dialog {
                margin: 10px;
                max-width: calc(100% - 20px);
              }
              .brutal-modal-header {
                padding: 12px 16px !important;
                flex-wrap: wrap;
              }
              .brutal-modal-header .modal-title {
                font-size: 20px !important;
              }
              .brutal-modal-body {
                padding: 16px;
                max-height: calc(100vh - 200px);
                overflow-y: auto;
                -webkit-overflow-scrolling: touch;
              }
              .brutal-modal-footer {
                padding: 12px 16px !important;
                flex-wrap: wrap;
                gap: 8px;
              }
              .brutal-modal-footer .btn {
                flex: 1;
                min-width: 0;
                min-height: 44px;
              }
              .brutal-modal .row {
                margin-left: 0;
                margin-right: 0;
              }
              .brutal-modal .row > * {
                padding-left: 0;
                padding-right: 0;
              }
              .brutal-modal .form-group {
                margin-bottom: 16px;
              }
              .brutal-modal .brutal-subheading {
                font-size: 14px;
                margin-bottom: 8px;
              }
              .brutal-modal .brutal-form-control {
                font-size: 16px;
                min-height: 44px;
              }
              .brutal-modal .d-flex.gap-2 {
                flex-direction: column;
                gap: 8px !important;
              }
              .brutal-modal .d-flex.gap-2 .btn {
                width: 100%;
              }
            }
            
            @media (max-width: 575px) {
              .brutal-modal .modal-dialog {
                margin: 5px;
                max-width: calc(100% - 10px);
              }
              .brutal-modal-body {
                padding: 12px;
                max-height: calc(100vh - 150px);
              }
              .brutal-modal-header .modal-title {
                font-size: 18px !important;
              }
            }
          `}</style>
        </Modal>
        )}
      </Container>
    </>
  );
}
